/**
 * Capital second life (§1.11). From 10:00 the first blow that would destroy a capital — a hit
 * that ruins it or creep that captures its hex — leaves it standing at `criticalLevel` and makes
 * it CRITICAL (a public `capitalCritical` phase event). Hits that land within the grace (the rest
 * of the same wave) cost nothing; every later level-costing hit restarts the window; a blow that
 * would destroy a critical capital destroys it (the victory system then eliminates its player).
 * A critical capital not hit again for `criticalTicks` recovers (`capitalRecovered`).
 */
import { BALANCE } from '../balance.js';
import { capitalsExposed, setCityLevel } from '../entities.js';
import type { CityEntity, HexIdx, MatchState, PlayerId, PlayerState, SimEvent } from '../types.js';

const K = BALANCE.capital;

/** The second life is on (BALANCE.capital.criticalTicks > 0). */
export function secondLife(): boolean {
  return K.criticalTicks > 0;
}

/** The player's capital is critical this tick. */
export function capitalCritical(state: MatchState, p: PlayerState): boolean {
  return state.tick < p.capitalCriticalUntil;
}

/** Inside the grace after going critical: further blows cost nothing. */
function inGrace(state: MatchState, p: PlayerState): boolean {
  return capitalCritical(state, p) && state.tick < p.capitalCriticalFrom + K.criticalGraceTicks;
}

/** (Re)start the critical window and tell everyone. */
function markCritical(state: MatchState, p: PlayerState, events: SimEvent[], fresh: boolean): void {
  if (fresh) {
    p.capitalCriticalFrom = state.tick;
    state.stats.players[p.id - 1].capitalCriticals++;
  }
  p.capitalCriticalUntil = state.tick + K.criticalTicks;
  events.push({ type: 'phase', phase: { kind: 'capitalCritical', a: p.id, b: Math.round(K.criticalTicks / 10), tick: state.tick } });
}

/**
 * Level a capital city keeps after a blow that would take it to `lvl` (nukes). Before 10:00 a
 * capital is hardened (L1 at worst); from 10:00 the first lethal blow makes it critical at
 * `criticalLevel`, the rest of that wave is absorbed, and a lethal blow while critical returns
 * `lvl` (≤ 0: ruined).
 */
export function capitalHitLevel(state: MatchState, p: PlayerState, city: CityEntity, lvl: number, events: SimEvent[]): number {
  if (!capitalsExposed(state)) return Math.max(1, lvl);
  if (!secondLife()) return lvl;
  if (inGrace(state, p)) return city.level;
  if (capitalCritical(state, p)) {
    if (lvl >= 1 && lvl < city.level) markCritical(state, p, events, false);
    return lvl;
  }
  if (lvl >= 1) return lvl;
  enterCritical(state, p, city, events);
  return K.criticalLevel;
}

/**
 * Creep is about to capture the capital hex of player `p` (from 10:00). Returns true when the
 * capture is refused: the capital goes critical instead (capture progress restarts).
 */
export function capitalResistsCapture(state: MatchState, p: PlayerState, city: CityEntity | null, events: SimEvent[]): boolean {
  if (!secondLife() || (capitalCritical(state, p) && !inGrace(state, p))) return false;
  if (!capitalCritical(state, p)) enterCritical(state, p, city, events);
  return true;
}

function enterCritical(state: MatchState, p: PlayerState, city: CityEntity | null, events: SimEvent[]): void {
  markCritical(state, p, events, true);
  if (!city) return;
  if (city.level > K.criticalLevel) setCityLevel(state, city, K.criticalLevel);
  if (K.criticalBraceReady && city.braceReadyTick > state.tick) city.braceReadyTick = state.tick;
}

/** Critical windows that ran out without another hit: the capital recovers. */
export function capitalSystem(state: MatchState, events: SimEvent[]): void {
  for (const p of state.players) {
    if (p.capitalCriticalUntil < 0 || state.tick < p.capitalCriticalUntil) continue;
    p.capitalCriticalUntil = -1;
    if (p.eliminatedTick >= 0) continue;
    state.stats.players[p.id - 1].capitalRecoveries++;
    events.push({ type: 'phase', phase: { kind: 'capitalRecovered', a: p.id, b: 0, tick: state.tick } });
  }
}

/** The finished city on hex h, null if none. */
export function capitalCity(state: MatchState, h: HexIdx): CityEntity | null {
  const sid = state.structId[h];
  const e = sid >= 0 ? state.entities.get(sid) : undefined;
  return e && e.kind === 'city' && e.buildDoneTick <= state.tick ? e : null;
}

/** Players whose capital is critical (for views / tests). */
export function criticalPlayers(state: MatchState): PlayerId[] {
  return state.players.filter((p) => p.eliminatedTick < 0 && capitalCritical(state, p)).map((p) => p.id);
}
